(() => {
  'use strict';

  // Shared presentation-only contract for Customily's native image method and
  // upload controls. Customily retains the real inputs, labels, value events,
  // uploads, preview, FormData, metadata, and submit lifecycle.
  const METHOD_META = Object.freeze({
    'Upload Image': Object.freeze({ title: 'Full Image', note: 'Use a photo or full-color graphic.', icon: 'I', order: 1, iconImage: 'GS-Asher-20260629-Image.webp' }),
    'Upload Logo': Object.freeze({ title: 'Logo', note: 'Best for brand marks and clean vector art.', icon: 'L', order: 2, iconImage: 'GS-Asher-20260629-Logo.webp' }),
    'Upload Face Image': Object.freeze({ title: 'Face Cut', note: 'Crop a face or subject for novelty art.', icon: 'F', order: 3, iconImage: 'GS-Asher-20260629-Facecut.webp' }),
    'Upload Image & Remove Background': Object.freeze({ title: 'BG Remover', note: 'Use when the art needs cleanup.', icon: 'B', order: 4, iconImage: 'GS-Asher-20260629-Background-Remover.webp' }),
  });

  const CONTRACT_MARK = 'gsImageSelectorContract';
  const METHOD_SELECTOR = 'input[type="radio"]';
  const FILE_SELECTOR = 'input[type="file"]';
  const UPLOAD_ICON_IMAGE = 'https://cdn.shopify.com/s/files/1/0759/0582/7052/files/Frame_29.png?v=1751518489';
  const SCRIPT_SRC = typeof document !== 'undefined' ? (document.currentScript?.src || '') : '';

  function themeAssetUrl(filename) {
    if (!filename) return '';
    try { return SCRIPT_SRC ? new URL(filename, SCRIPT_SRC).href : filename; } catch (_) { return filename; }
  }

  function methodForInput(input) {
    return METHOD_META[input?.value] ?? null;
  }

  function labelFor(input, doc) {
    if (!input) return null;
    const local = input.closest?.('label') || input.closest?.('.customily-swatch')?.querySelector?.('label') || input.closest?.('.swatch')?.querySelector?.('label');
    if (local) return local;
    if (input.id && doc?.querySelector) {
      try {
        const label = doc.querySelector(`label[for="${input.id}"]`);
        if (label) return label;
      } catch (_) {}
    }
    return null;
  }

  function cardFor(input, label) {
    return input?.closest?.('.customily-swatch') || input?.closest?.('.swatch') || label || input;
  }

  function optionFor(input) {
    return input?.closest?.('.customily_option') || input?.closest?.('.customily_section') || input?.parentElement || null;
  }

  function mark(node, kind) {
    if (node?.dataset) node.dataset[CONTRACT_MARK] = kind;
  }

  function methodCopyFor(label, meta, doc) {
    const nativeCopy = label?.querySelector?.('.label-txt');
    if (nativeCopy) return nativeCopy;
    const fallback = doc?.createElement?.('span');
    if (!fallback) return null;
    fallback.className = 'label-txt';
    fallback.dataset.gsImageSelectorFallback = 'true';
    fallback.textContent = meta.title;
    mark(fallback, 'method-copy-fallback');
    label.append?.(fallback);
    return fallback;
  }

  function ensureUploadIcon(option, doc) {
    if (!option || !doc?.createElement) return null;
    let wrapper = Array.from(option.children ?? []).find(child => child.classList?.contains?.('added-icon-wrapper')) || null;
    if (!wrapper) {
      wrapper = doc.createElement('span');
      wrapper.className = 'added-icon-wrapper';
      wrapper.setAttribute?.('aria-hidden', 'true');
      mark(wrapper, 'upload-icon-wrapper-fallback');
      const first = option.children?.[0] ?? null;
      if (first && option.insertBefore) option.insertBefore(wrapper, first);
      else option.append?.(wrapper);
    }

    let image = wrapper.querySelector?.('img.added-icon') || wrapper.querySelector?.('img') || null;
    let imageOwned = false;
    if (!image) {
      image = doc.createElement('img');
      image.className = 'added-icon';
      image.setAttribute?.('alt', '');
      mark(image, 'upload-icon-image-fallback');
      wrapper.append?.(image);
      imageOwned = true;
    }
    if (!image.getAttribute?.('src')) {
      if (!imageOwned) mark(image, 'upload-icon-image-repaired');
      image.setAttribute?.('src', UPLOAD_ICON_IMAGE);
    }
    return wrapper;
  }

  function apply(root, doc = typeof document !== 'undefined' ? document : null) {
    if (!root) return false;
    for (const input of root.querySelectorAll?.(METHOD_SELECTOR) ?? []) {
      const meta = methodForInput(input);
      if (!meta) continue;
      const label = labelFor(input, doc);
      const card = cardFor(input, label);
      const option = optionFor(input);
      if (!label || !card || !option) continue;

      option.dataset.gsProgressiveRole = 'method';
      mark(option, 'method-role');
      for (const target of new Set([input, card, label].filter(Boolean))) {
        target.dataset.gsImageMethodTitle = meta.title;
        target.dataset.gsImageMethodOrder = String(meta.order);
        mark(target, 'method');
      }
      label.style?.setProperty?.('--gs-image-method-art', `url("${themeAssetUrl(meta.iconImage)}")`);
      if (card.style) card.style.order = String(meta.order);
      const nativeImage = label.querySelector?.('img');
      if (nativeImage && meta.iconImage) {
        if (!Object.prototype.hasOwnProperty.call(nativeImage.dataset, 'gsImageSelectorOriginalSrc')) {
          nativeImage.dataset.gsImageSelectorOriginalSrc = nativeImage.getAttribute?.('src') || '';
        }
        mark(nativeImage, 'method-image');
        if (!nativeImage.classList?.contains?.('gs-customily-provided-icon')) {
          nativeImage.classList?.add?.('gs-customily-provided-icon');
          nativeImage.dataset.gsImageSelectorAddedProvidedIcon = 'true';
        }
        nativeImage.setAttribute?.('src', themeAssetUrl(meta.iconImage));
        nativeImage.setAttribute?.('alt', '');
      }
      const nativeCopy = methodCopyFor(label, meta, doc);
      if (nativeCopy && nativeCopy.textContent !== meta.title) {
        if (!Object.prototype.hasOwnProperty.call(nativeCopy.dataset, 'gsImageSelectorOriginalCopy')) {
          nativeCopy.dataset.gsImageSelectorOriginalCopy = nativeCopy.textContent;
        }
        mark(nativeCopy, 'method-copy');
        nativeCopy.textContent = meta.title;
      }
    }

    for (const input of root.querySelectorAll?.(FILE_SELECTOR) ?? []) {
      const option = optionFor(input);
      if (!option) continue;
      option.dataset.gsProgressiveRole = 'upload';
      mark(option, 'upload-role');
      mark(input, 'upload');
      ensureUploadIcon(option, doc);
    }

    root.classList?.add('gs-customily-image-selector-root');
    doc?.documentElement?.classList?.add('gs-customily-image-selector-enabled');
    return true;
  }

  function clear(root, doc = typeof document !== 'undefined' ? document : null) {
    for (const node of root?.querySelectorAll?.('[data-gs-image-selector-contract]') ?? []) {
      const kind = node.dataset?.[CONTRACT_MARK];
      if (kind === 'method-role' || kind === 'upload-role') delete node.dataset.gsProgressiveRole;
      if (kind === 'method') {
        delete node.dataset.gsImageMethodTitle;
        delete node.dataset.gsImageMethodOrder;
        if (node.style) node.style.order = '';
        node.style?.removeProperty?.('--gs-image-method-art');
      }
      if (kind === 'method-copy') {
        if (Object.prototype.hasOwnProperty.call(node.dataset, 'gsImageSelectorOriginalCopy')) {
          node.textContent = node.dataset.gsImageSelectorOriginalCopy;
          delete node.dataset.gsImageSelectorOriginalCopy;
        }
      }
      if (kind === 'method-copy-fallback') node.remove?.();
      if (kind === 'upload-icon-wrapper-fallback' || kind === 'upload-icon-image-fallback') node.remove?.();
      if (kind === 'upload-icon-image-repaired') node.removeAttribute?.('src');
      if (kind === 'method-image') {
        const original = node.dataset.gsImageSelectorOriginalSrc;
        if (original) node.setAttribute?.('src', original);
        else node.removeAttribute?.('src');
        delete node.dataset.gsImageSelectorOriginalSrc;
        if (node.dataset.gsImageSelectorAddedProvidedIcon === 'true') {
          node.classList?.remove?.('gs-customily-provided-icon');
          delete node.dataset.gsImageSelectorAddedProvidedIcon;
        }
      }
      delete node.dataset[CONTRACT_MARK];
    }
    root?.classList?.remove('gs-customily-image-selector-root');
    doc?.documentElement?.classList?.remove('gs-customily-image-selector-enabled');
  }

  const api = Object.freeze({ METHOD_META, METHOD_SELECTOR, FILE_SELECTOR, UPLOAD_ICON_IMAGE, themeAssetUrl, methodForInput, labelFor, cardFor, optionFor, methodCopyFor, ensureUploadIcon, apply, clear });
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.GSCustomilyImageSelector = api;
})();
